import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Package, Shield, Truck, RefreshCw, Search, ChevronDown, ChevronUp, CheckCircle, CircleDot, Circle, MapPin, Calendar, Filter, Lock, LogOut, Users, UserPlus, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRACKING_STEPS = [
  { key: 'order_received', label: 'Order Received' },
  { key: 'gear_prepared', label: 'Gear Prepared' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'in_transit', label: 'In Transit' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'returned', label: 'Returned' },
  { key: 'completed', label: 'Completed' },
];

const DEPOSIT_OPTIONS = [
  { key: 'held', label: 'Held', color: 'bg-amber-100 text-amber-700' },
  { key: 'under_review', label: 'Under Review', color: 'bg-blue-100 text-blue-700' },
  { key: 'refunded', label: 'Refunded', color: 'bg-green-100 text-green-700' },
  { key: 'forfeited', label: 'Forfeited', color: 'bg-red-100 text-red-700' },
];

const AdminOrderCard = ({ order, onRefresh, adminToken }) => {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);

  const itemNames = order.items?.map(i => i.title).join(', ') || 'Rental Order';
  const createdDate = order.created_at ? new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';
  const currentStepIndex = TRACKING_STEPS.findIndex(s => s.key === order.order_status);

  const adminHeaders = { Authorization: `Bearer ${adminToken}` };

  const updateTracking = async (newStatus) => {
    setUpdating(true);
    try {
      const resp = await axios.patch(`${API}/orders/${order.session_id}/status`, { status: newStatus, note: `Updated via admin panel` }, { headers: adminHeaders });
      const emailNote = resp.data?.email_sent ? ' (customer emailed)' : order.customer_email ? '' : ' (no email on file)';
      toast.success(`Tracking updated to "${TRACKING_STEPS.find(s => s.key === newStatus)?.label}"${emailNote}`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update tracking');
    } finally { setUpdating(false); }
  };

  const updateDeposit = async (newStatus) => {
    setUpdating(true);
    try {
      const resp = await axios.patch(`${API}/orders/${order.session_id}/deposit`, { status: newStatus }, { headers: adminHeaders });
      const emailNote = resp.data?.email_sent ? ' (customer emailed)' : '';
      toast.success(`Deposit marked as "${DEPOSIT_OPTIONS.find(d => d.key === newStatus)?.label}"${emailNote}`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update deposit');
    } finally { setUpdating(false); }
  };

  return (
    <div className="bg-white border border-gray-200 hover:border-gray-300 transition-colors" data-testid={`admin-order-${order.session_id}`}>
      {/* Header row */}
      <div className="p-4 flex flex-col lg:flex-row lg:items-center gap-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        {/* Left: Order info */}
        <div className="flex-1 min-w-0">
          <p className="font-['Barlow_Condensed'] text-base font-semibold uppercase tracking-tight truncate" style={{ color: '#333' }}>{itemNames}</p>
          <div className="flex items-center gap-3 text-xs mt-1" style={{ color: '#999' }}>
            <span>{createdDate}</span>
            <span>&middot;</span>
            <span>{order.rental_days}d rental</span>
            <span>&middot;</span>
            <span>{order.customer_email || 'No email'}</span>
          </div>
        </div>

        {/* Middle: Status chips */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`text-xs ${order.order_status === 'completed' ? 'bg-green-100 text-green-700' : order.order_status === 'delivered' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
            {TRACKING_STEPS.find(s => s.key === order.order_status)?.label || 'Unknown'}
          </Badge>
          {order.deposit_amount > 0 && (
            <Badge className={DEPOSIT_OPTIONS.find(d => d.key === order.deposit_status)?.color || 'bg-gray-100 text-gray-500'}>
              Deposit: {DEPOSIT_OPTIONS.find(d => d.key === order.deposit_status)?.label || order.deposit_status}
            </Badge>
          )}
        </div>

        {/* Right: Amount + expand */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="font-['Barlow_Condensed'] text-xl font-bold text-[#C6A87C]">${order.amount?.toFixed(2)}</span>
          {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 pb-5 pt-4 space-y-5" data-testid={`admin-details-${order.session_id}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Left column: Tracking controls */}
            <div className="bg-gray-50 border border-gray-200 p-4">
              <h3 className="font-['Barlow_Condensed'] text-sm font-semibold uppercase tracking-widest mb-3 flex items-center gap-2" style={{ color: '#333' }}>
                <Truck className="w-4 h-4 text-[#C6A87C]" /> Update Tracking
              </h3>
              <div className="space-y-1.5">
                {TRACKING_STEPS.map((step, index) => {
                  const isCompleted = index < currentStepIndex;
                  const isCurrent = index === currentStepIndex;
                  const isNext = index === currentStepIndex + 1;
                  return (
                    <button
                      key={step.key}
                      disabled={updating || isCompleted || isCurrent}
                      onClick={(e) => { e.stopPropagation(); updateTracking(step.key); }}
                      className={`w-full flex items-center gap-3 p-2.5 text-left text-sm transition-colors rounded ${
                        isCurrent ? 'bg-[#C6A87C]/10 border border-[#C6A87C]/30 font-semibold' :
                        isCompleted ? 'opacity-50 cursor-not-allowed' :
                        isNext ? 'bg-white border border-gray-200 hover:border-[#C6A87C] hover:bg-[#C6A87C]/5 cursor-pointer' :
                        'bg-white border border-gray-100 hover:border-gray-300 cursor-pointer'
                      }`}
                      data-testid={`tracking-btn-${step.key}`}
                    >
                      {isCompleted ? <CheckCircle className="w-4 h-4 text-[#C6A87C]" /> :
                       isCurrent ? <CircleDot className="w-4 h-4 text-[#C6A87C]" /> :
                       <Circle className="w-4 h-4 text-gray-300" />}
                      <span style={{ color: isCurrent ? '#C6A87C' : isCompleted ? '#999' : '#333' }}>{step.label}</span>
                      {isNext && <span className="ml-auto text-xs text-[#C6A87C] font-medium">Next</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right column: Deposit + Order info */}
            <div className="space-y-4">
              {/* Deposit controls */}
              {order.deposit_amount > 0 && (
                <div className="bg-gray-50 border border-gray-200 p-4">
                  <h3 className="font-['Barlow_Condensed'] text-sm font-semibold uppercase tracking-widest mb-3 flex items-center gap-2" style={{ color: '#333' }}>
                    <Shield className="w-4 h-4 text-[#C6A87C]" /> Security Deposit (${order.deposit_amount?.toFixed(2)})
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {DEPOSIT_OPTIONS.map(opt => (
                      <button
                        key={opt.key}
                        disabled={updating || order.deposit_status === opt.key}
                        onClick={(e) => { e.stopPropagation(); updateDeposit(opt.key); }}
                        className={`p-2.5 text-sm font-medium border transition-colors rounded ${
                          order.deposit_status === opt.key
                            ? `${opt.color} border-current font-bold`
                            : 'bg-white border-gray-200 hover:border-gray-400 cursor-pointer'
                        }`}
                        style={{ color: order.deposit_status === opt.key ? undefined : '#333' }}
                        data-testid={`deposit-btn-${opt.key}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {/* Shopify refund instructions */}
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-xs space-y-1" data-testid="shopify-refund-instructions">
                    <p className="font-semibold text-blue-700">How to refund the deposit via Shopify:</p>
                    <ol className="list-decimal ml-4 space-y-0.5" style={{ color: '#555' }}>
                      <li>Go to <strong>Shopify Admin</strong> &rarr; Orders &rarr; find this order</li>
                      <li>Click <strong>Refund</strong> &rarr; enter <strong>${order.deposit_amount?.toFixed(2)}</strong></li>
                      <li>Confirm the partial refund in Shopify</li>
                      <li>Then click <strong>"Refunded"</strong> above to update the status &amp; notify the customer</li>
                    </ol>
                  </div>
                  {order.customer_email && (
                    <p className="mt-2 text-xs" style={{ color: '#999' }}>
                      Customer will be auto-emailed when deposit status changes to Refunded, Forfeited, or Under Review.
                    </p>
                  )}
                  {!order.customer_email && (
                    <p className="mt-2 text-xs text-amber-600">
                      No customer email on file — status update emails cannot be sent.
                    </p>
                  )}
                </div>
              )}

              {/* Order details */}
              <div className="bg-gray-50 border border-gray-200 p-4 text-sm space-y-2">
                <div className="flex justify-between">
                  <span style={{ color: '#666' }}>Rental Period</span>
                  <span style={{ color: '#333' }}>{order.rental_start} to {order.rental_end}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: '#666' }}>Return By</span>
                  <span className="text-[#C6A87C] font-semibold">{order.return_date}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: '#666' }}>Rental Total</span>
                  <span style={{ color: '#333' }}>${order.rental_total?.toFixed(2)}</span>
                </div>
                {order.discount_amount > 0 && (
                  <div className="flex justify-between text-[#C6A87C]">
                    <span>Discount ({order.discount_percent}%)</span>
                    <span>-${order.discount_amount?.toFixed(2)}</span>
                  </div>
                )}
                {order.insurance_total > 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: '#666' }}>Insurance</span>
                    <span style={{ color: '#333' }}>${order.insurance_total?.toFixed(2)}</span>
                  </div>
                )}
                {order.shipping_fee > 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: '#666' }}>Expedited Shipping</span>
                    <span style={{ color: '#333' }}>${order.shipping_fee?.toFixed(2)}</span>
                  </div>
                )}
              </div>

              {/* Shipping info */}
              {order.shipping_info && (
                <div className="bg-gray-50 border border-gray-200 p-4 text-sm">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-[#C6A87C] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold" style={{ color: '#333' }}>{order.shipping_info.recipient_name}</p>
                      <p style={{ color: '#666' }} className="capitalize">{order.shipping_info.destination_type} &middot; {order.shipping_info.full_address}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status history */}
          {order.status_history?.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 p-4">
              <h3 className="font-['Barlow_Condensed'] text-sm font-semibold uppercase tracking-widest mb-2" style={{ color: '#333' }}>Activity Log</h3>
              <div className="space-y-1.5 text-xs max-h-32 overflow-y-auto">
                {order.status_history.slice().reverse().map((entry, i) => (
                  <div key={i} className="flex items-center gap-2 py-1">
                    <span className="text-[#C6A87C] font-mono w-36 flex-shrink-0">
                      {new Date(entry.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <Badge className="bg-gray-200 text-gray-600 text-xs">{entry.status.replace(/_/g, ' ')}</Badge>
                    {entry.note && <span style={{ color: '#666' }}>{entry.note}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TeamMemberCard = ({ member, adminHeaders, onRefresh, onRemove }) => {
  const [resetting, setResetting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetting(true);
    try {
      await axios.patch(`${API}/auth/admin/team/${member.member_id}/password`, { new_password: newPassword }, { headers: adminHeaders });
      toast.success(`Password reset for ${member.name}`);
      setShowResetForm(false);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally { setResetting(false); }
  };

  return (
    <div className="bg-white border border-gray-200 p-4" data-testid={`team-member-${member.member_id}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-sm" style={{ color: '#333' }}>{member.name}</p>
          <p className="text-xs" style={{ color: '#999' }}>{member.email} &middot; Added {new Date(member.created_at).toLocaleDateString()}</p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowResetForm(!showResetForm)}
            className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border border-gray-200 hover:border-[#C6A87C] hover:text-[#C6A87C] transition-colors"
            style={{ color: '#666' }}
            data-testid={`reset-pw-btn-${member.member_id}`}
          >
            {showResetForm ? 'Cancel' : 'Reset Password'}
          </button>
          <button
            onClick={() => onRemove(member.member_id, member.name)}
            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
            data-testid={`remove-member-${member.member_id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {showResetForm && (
        <form onSubmit={handleReset} className="mt-3 flex items-center gap-2 pt-3 border-t border-gray-100">
          <div className="relative flex-1">
            <input
              type={showNewPw ? 'text' : 'password'}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="New password (min 6 chars)"
              className="w-full p-2.5 pr-10 border border-gray-200 text-sm focus:outline-none focus:border-[#C6A87C]"
              style={{ color: '#333', background: '#fff' }}
              data-testid={`new-pw-input-${member.member_id}`}
            />
            <button type="button" onClick={() => setShowNewPw(!showNewPw)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showNewPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
          <Button type="submit" disabled={resetting || newPassword.length < 6}
            className="bg-[#C6A87C] text-white hover:bg-[#b09568] text-xs font-bold uppercase"
            data-testid={`confirm-reset-btn-${member.member_id}`}
          >
            {resetting ? 'Resetting...' : 'Reset'}
          </Button>
        </form>
      )}
    </div>
  );
};


const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('admin_auth') === 'true';
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem('admin_token') || '');
  const [isOwner, setIsOwner] = useState(() => sessionStorage.getItem('admin_is_owner') === 'true');
  const [activeTab, setActiveTab] = useState('orders');

  // Team state
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [addingMember, setAddingMember] = useState(false);

  const adminHeaders = { Authorization: `Bearer ${adminToken}` };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/auth/admin/login`, { email, password });
      if (response.data.authenticated && response.data.token) {
        sessionStorage.setItem('admin_auth', 'true');
        sessionStorage.setItem('admin_token', response.data.token);
        sessionStorage.setItem('admin_is_owner', response.data.is_owner ? 'true' : 'false');
        setAdminToken(response.data.token);
        setIsOwner(!!response.data.is_owner);
        setIsAuthenticated(true);
        toast.success('Welcome, Admin');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally { setLoginLoading(false); }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_auth');
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_is_owner');
    setIsAuthenticated(false);
    setAdminToken('');
    setIsOwner(false);
    setOrders([]);
    setTeamMembers([]);
    toast.success('Logged out');
  };

  const fetchOrders = useCallback(async () => {
    if (!isAuthenticated || !adminToken) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setOrders(response.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleLogout();
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to load orders');
      }
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, adminToken]);

  const fetchTeamMembers = useCallback(async () => {
    if (!isAuthenticated || !adminToken) return;
    setTeamLoading(true);
    try {
      const response = await axios.get(`${API}/auth/admin/team`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setTeamMembers(response.data);
    } catch (err) {
      // Non-owners may get 403, that's fine
    } finally { setTeamLoading(false); }
  }, [isAuthenticated, adminToken]);

  const addTeamMember = async (e) => {
    e.preventDefault();
    if (!newMember.name || !newMember.email || !newMember.password) return;
    setAddingMember(true);
    try {
      await axios.post(`${API}/auth/admin/team`, newMember, { headers: adminHeaders });
      toast.success(`${newMember.name} added to the team`);
      setNewMember({ name: '', email: '', password: '' });
      fetchTeamMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add team member');
    } finally { setAddingMember(false); }
  };

  const removeTeamMember = async (memberId, name) => {
    if (!window.confirm(`Remove ${name} from the team?`)) return;
    try {
      await axios.delete(`${API}/auth/admin/team/${memberId}`, { headers: adminHeaders });
      toast.success(`${name} removed`);
      fetchTeamMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  useEffect(() => { if (isAuthenticated) fetchOrders(); }, [fetchOrders, isAuthenticated]);
  useEffect(() => { if (isAuthenticated && activeTab === 'team') fetchTeamMembers(); }, [fetchTeamMembers, isAuthenticated, activeTab]);

  const filtered = orders.filter(order => {
    const matchesSearch = searchTerm === '' ||
      (order.customer_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.items || []).some(i => (i.title || '').toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.session_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.shipping_info?.recipient_name || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'deposit_held' && order.deposit_status === 'held') ||
      (statusFilter === 'needs_action' && order.order_status !== 'completed') ||
      order.order_status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: orders.length,
    needsAction: orders.filter(o => o.order_status && o.order_status !== 'completed').length,
    depositsHeld: orders.filter(o => o.deposit_status === 'held').length,
    revenue: orders.reduce((sum, o) => sum + (o.amount || 0), 0),
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white pt-24" data-testid="admin-login-page">
        <div className="px-6 md:px-12 py-12">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-[#C6A87C]" />
              </div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase tracking-tight" style={{ color: '#333' }}>
                Admin Access
              </h1>
              <p className="text-sm mt-2" style={{ color: '#666' }}>Sign in to manage orders and deposits</p>
            </div>
            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest block mb-1.5" style={{ color: '#999' }}>Email</label>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  className="w-full p-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C6A87C]"
                  style={{ color: '#333', background: '#fff' }}
                  placeholder="admin@email.com"
                  data-testid="admin-email-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest block mb-1.5" style={{ color: '#999' }}>Password</label>
                <input
                  type="password" value={password} onChange={e => setPassword(e.target.value)} required
                  className="w-full p-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C6A87C]"
                  style={{ color: '#333', background: '#fff' }}
                  placeholder="Enter password"
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit" disabled={loginLoading}
                className="w-full bg-[#1a1a1a] text-white hover:bg-[#333] font-bold uppercase tracking-wider h-12"
                data-testid="admin-login-btn"
              >
                {loginLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="admin-page">
      <div className="px-6 md:px-12 py-12">
        <div className="max-w-[1400px] mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-4">
            <div>
              <p className="text-sm text-[#C6A87C] uppercase tracking-widest mb-2">Admin</p>
              <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight" style={{ color: '#333' }}>
                {activeTab === 'orders' ? 'Order Management' : 'Team Members'}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {activeTab === 'orders' && (
                <Button onClick={fetchOrders} disabled={loading} variant="outline" className="border-gray-200 hover:border-[#C6A87C]" data-testid="refresh-orders-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              )}
              <Button onClick={handleLogout} variant="outline" className="border-gray-200 hover:border-red-300 hover:text-red-600" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-8 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-5 py-2.5 text-sm font-semibold uppercase tracking-wider transition-colors ${
                activeTab === 'orders' ? 'text-[#C6A87C] border-b-2 border-[#C6A87C]' : 'text-gray-400 hover:text-gray-600'
              }`}
              data-testid="admin-tab-orders"
            >
              <Package className="w-4 h-4 inline mr-2" />Orders
            </button>
            {isOwner && (
              <button
                onClick={() => setActiveTab('team')}
                className={`px-5 py-2.5 text-sm font-semibold uppercase tracking-wider transition-colors ${
                  activeTab === 'team' ? 'text-[#C6A87C] border-b-2 border-[#C6A87C]' : 'text-gray-400 hover:text-gray-600'
                }`}
                data-testid="admin-tab-team"
              >
                <Users className="w-4 h-4 inline mr-2" />Team
              </button>
            )}
          </div>

          {/* Team Tab */}
          {activeTab === 'team' && isOwner && (
            <div className="space-y-8">
              {/* Add Member Form */}
              <div className="bg-gray-50 border border-gray-200 p-6">
                <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2" style={{ color: '#333' }}>
                  <UserPlus className="w-5 h-5 text-[#C6A87C]" /> Add Team Member
                </h2>
                <form onSubmit={addTeamMember} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest block mb-1.5" style={{ color: '#999' }}>Name</label>
                    <input
                      type="text" value={newMember.name} onChange={e => setNewMember(p => ({ ...p, name: e.target.value }))} required
                      placeholder="Team member name"
                      className="w-full p-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C6A87C]"
                      style={{ color: '#333', background: '#fff' }}
                      data-testid="team-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest block mb-1.5" style={{ color: '#999' }}>Email</label>
                    <input
                      type="email" value={newMember.email} onChange={e => setNewMember(p => ({ ...p, email: e.target.value }))} required
                      placeholder="email@example.com"
                      className="w-full p-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C6A87C]"
                      style={{ color: '#333', background: '#fff' }}
                      data-testid="team-email-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest block mb-1.5" style={{ color: '#999' }}>Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'} value={newMember.password}
                        onChange={e => setNewMember(p => ({ ...p, password: e.target.value }))} required
                        placeholder="Secure password"
                        className="w-full p-3 pr-10 border border-gray-200 text-sm focus:outline-none focus:border-[#C6A87C]"
                        style={{ color: '#333', background: '#fff' }}
                        data-testid="team-password-input"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="sm:col-span-3">
                    <Button type="submit" disabled={addingMember}
                      className="bg-[#1a1a1a] text-white hover:bg-[#333] font-bold uppercase tracking-wider"
                      data-testid="add-team-member-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" /> {addingMember ? 'Adding...' : 'Add Member'}
                    </Button>
                  </div>
                </form>
              </div>

              {/* Team List */}
              <div>
                <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight mb-4" style={{ color: '#333' }}>
                  Current Team ({teamMembers.length})
                </h2>
                {teamLoading ? (
                  <div className="animate-pulse space-y-2">
                    {[1, 2].map(i => <div key={i} className="bg-gray-100 h-16 rounded" />)}
                  </div>
                ) : teamMembers.length === 0 ? (
                  <div className="bg-gray-50 border border-gray-200 p-8 text-center">
                    <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm" style={{ color: '#666' }}>No team members yet. Add someone above to grant admin access.</p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="team-members-list">
                    {teamMembers.map(member => (
                      <TeamMemberCard
                        key={member.member_id}
                        member={member}
                        adminHeaders={adminHeaders}
                        onRefresh={fetchTeamMembers}
                        onRemove={removeTeamMember}
                      />
                    ))}
                  </div>
                )}
                {/* Owner note */}
                <div className="mt-4 p-3 bg-[#C6A87C]/10 border border-[#C6A87C]/20 text-xs" style={{ color: '#666' }}>
                  <strong>Owner Account:</strong> Sean@Brooklynmayd.com (you) — cannot be removed. Team members have the same admin access to manage orders and deposits.
                </div>
              </div>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="bg-gray-50 border border-gray-200 p-4" data-testid="admin-stat-total">
              <p className="text-xs uppercase tracking-widest" style={{ color: '#999' }}>Total Orders</p>
              <p className="text-2xl font-bold mt-1" style={{ color: '#333' }}>{stats.total}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 p-4" data-testid="admin-stat-active">
              <p className="text-xs uppercase tracking-widest" style={{ color: '#999' }}>Needs Action</p>
              <p className="text-2xl font-bold mt-1 text-[#C6A87C]">{stats.needsAction}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 p-4" data-testid="admin-stat-deposits">
              <p className="text-xs uppercase tracking-widest" style={{ color: '#999' }}>Deposits Held</p>
              <p className="text-2xl font-bold mt-1 text-amber-600">{stats.depositsHeld}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 p-4" data-testid="admin-stat-revenue">
              <p className="text-xs uppercase tracking-widest" style={{ color: '#999' }}>Total Revenue</p>
              <p className="text-2xl font-bold mt-1" style={{ color: '#333' }}>${stats.revenue.toFixed(2)}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by email, name, item, or order ID..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 text-sm focus:outline-none focus:border-[#C6A87C]"
                style={{ color: '#333', background: '#fff' }}
                data-testid="admin-search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="py-2.5 px-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C6A87C] bg-white"
                style={{ color: '#333' }}
                data-testid="admin-status-filter"
              >
                <option value="all">All Orders</option>
                <option value="needs_action">Needs Action</option>
                <option value="deposit_held">Deposits Held</option>
                <option value="order_received">Order Received</option>
                <option value="gear_prepared">Gear Prepared</option>
                <option value="shipped">Shipped</option>
                <option value="in_transit">In Transit</option>
                <option value="delivered">Delivered</option>
                <option value="returned">Returned</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          {/* Order List */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-gray-50 border border-gray-200 p-5 animate-pulse">
                  <div className="h-4 bg-gray-200 w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 w-1/2"></div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 p-12 text-center">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-lg font-semibold" style={{ color: '#333' }}>
                {searchTerm || statusFilter !== 'all' ? 'No matching orders' : 'No orders yet'}
              </p>
              <p className="text-sm mt-1" style={{ color: '#666' }}>
                {searchTerm || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Orders will appear here when customers check out'}
              </p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="admin-orders-list">
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#999' }}>{filtered.length} order{filtered.length !== 1 ? 's' : ''}</p>
              {filtered.map(order => (
                <AdminOrderCard key={order.session_id} order={order} onRefresh={fetchOrders} adminToken={adminToken} />
              ))}
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
